/**
 * Two charts over one evolution axis: the threat a team settles at, and the
 * wave that level draws.
 *
 * There is no simulation on screen and no approach to watch. A real game does
 * not creep up from an empty pool — it opens with a send that puts evolution
 * and threat up together, and works down from there — so only the level
 * matters, and the level is what these show.
 *
 * Bosses are counted as 20 in the stack. That is the game's own conversion, not
 * a presentational one: `spawn_biters` prices a boss at `value x 20 x H` with
 * the comment "one boss is equal of 20 biters in theory". Counted as one they
 * would be a hairline; counted as 20 the band is the size of the thing it
 * represents. They are drawn as one band per tier rather than one band in
 * total, because which tier is affordable is the whole story of what happens
 * after 70% evolution.
 *
 * Three visual channels, so a band never depends on being matched to a legend
 * entry by colour alone: **hue is the tier**, a **lighter shade is a spitter**,
 * and a **hatch is a boss**.
 *
 * The evolution axis is the app's shared zoom, the same one the charts page
 * runs on — the control speaks in evolution already, so the window carries
 * across a page change instead of resetting to a different part of the game.
 *
 * The one assumption is an even game: both teams on the same threat, which is
 * `⌈7 × 0.5⌉ = 4` groups per attack. It is stated on the surface rather than
 * hidden behind a control.
 */

import { useDeferredValue, useMemo, useState, type ReactNode } from 'react';

import { ParentSize } from '@visx/responsive';

import {
  EVEN_GAME_GROUPS,
  PASSIVE_POWER_STEP,
  TIER_COLOURS,
  UNIT_KINDS,
  UNIT_TIERS,
  effectivePassivePower,
  formatCompact,
  formatEvo,
  gridSamples,
  passivePowerBounds,
  pricesKindsApart,
  steadyStateByEvo,
  type SteadyState,
  type UnitKind,
  type UnitTier,
} from '../../model';
import { ParamActions, ParamRow, ParamSlider } from '../ParamRow';
import { ScenarioReset } from '../ScenarioPanel';
import { ZoomControls } from '../ZoomControls';
import { useLab } from '../LabContext';
import { evoTicks } from '../ticks';
import { useWheelZoom } from '../useWheelZoom';

/** ai.lua:133 — `health_buff_equivalent_revive * 20` for a boss. */
const BOSS_WEIGHT = 20;

const MARGIN = { top: 10, right: 14, left: 58 };
const PLOT_HEIGHT = 128;
const AXIS_HEIGHT = 20;

const THREAT_COLOUR = '#7fd1ff';

/** The surface colour, used to separate adjacent bands and to cut the hatch. */
const SURFACE = '#141922';

const TIER_LABEL: Record<UnitTier, string> = {
  small: 'Small',
  medium: 'Medium',
  big: 'Big',
  behemoth: 'Behemoth',
};

/** Strongest first — the order bands are stacked in, bottom up. */
const STRONGEST_FIRST: UnitTier[] = [...UNIT_TIERS].reverse();

/** Mix a hex colour towards white. Spitters are the same hue, one step up. */
function lighten(hex: string, amount: number): string {
  const value = parseInt(hex.slice(1), 16);
  const mix = (shift: number) => {
    const channel = (value >> shift) & 0xff;
    return Math.round(channel + (255 - channel) * amount);
  };
  return `rgb(${mix(16)}, ${mix(8)}, ${mix(0)})`;
}

const SPITTER_LIGHTEN = 0.42;

const tierFill = (tier: UnitTier, kind: UnitKind | null) =>
  kind === 'spitter' ? lighten(TIER_COLOURS[tier], SPITTER_LIGHTEN) : TIER_COLOURS[tier];

/**
 * One stacked area. `kind` is null when the branch prices biters and spitters
 * the same and there is nothing to gain from separating them.
 */
interface Band {
  key: string;
  tier: UnitTier;
  kind: UnitKind | null;
  boss: boolean;
  label: string;
  colour: string;
  /** Fill as drawn — the colour itself, or the hatch pattern built from it. */
  fill: string;
}

function buildBands(split: boolean): Band[] {
  const kinds: (UnitKind | null)[] = split ? UNIT_KINDS : [null];
  const bands: Band[] = [];
  // Bosses lead so they sit at the bottom of the stack, then the normal units
  // above them; within each, strongest tier first.
  for (const boss of [true, false]) {
    for (const tier of STRONGEST_FIRST) {
      for (const kind of kinds) {
        const key = `${boss ? 'boss' : 'unit'}-${tier}-${kind ?? 'all'}`;
        const colour = tierFill(tier, kind);
        bands.push({
          key,
          tier,
          kind,
          boss,
          label: `${boss ? 'Boss ' : ''}${TIER_LABEL[tier].toLowerCase()}${
            kind ? ` ${kind}` : ''
          }`,
          colour,
          fill: boss ? `url(#${key})` : colour,
        });
      }
    }
  }
  return bands;
}

/** What a band contributes to the stack — bosses at their 20× weight. */
function bandValue(point: SteadyState, band: Band): number {
  const { wave } = point;
  if (band.kind === null) {
    return band.boss ? wave.bosses[band.tier] * BOSS_WEIGHT : wave.units[band.tier];
  }
  const counts = band.boss ? wave.bossesByKind[band.kind] : wave.unitsByKind[band.kind];
  return band.boss ? counts[band.tier] * BOSS_WEIGHT : counts[band.tier];
}

interface Tick {
  value: number;
  y: number;
}

function yTicks(max: number, height: number, steps: number): Tick[] {
  return Array.from({ length: steps + 1 }, (_, i) => {
    const value = (max * i) / steps;
    return { value, y: height - (value / (max || 1)) * height };
  });
}

/** Shared axis furniture, so both plots line up column for column. */
function PlotFrame({
  innerWidth,
  height,
  ticks,
  format,
  children,
}: {
  innerWidth: number;
  height: number;
  ticks: Tick[];
  format: (value: number) => string;
  children: ReactNode;
}) {
  return (
    <g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
      {ticks.map((tick) => (
        <g key={tick.value}>
          <line x1={0} x2={innerWidth} y1={tick.y} y2={tick.y} stroke="#212836" />
          <text x={-8} y={tick.y + 3.5} textAnchor="end" className="wave-axis-text">
            {format(tick.value)}
          </text>
        </g>
      ))}
      {children}
      <line x1={0} x2={innerWidth} y1={height} y2={height} stroke="#2c3444" />
    </g>
  );
}

interface Hover {
  index: number;
  /** Which panel the pointer is in, so the tooltip renders in that one. */
  panel: number;
}

/** Threat per minute, from a per-second figure. */
const perMinute = (perSecond: number) => formatCompact(perSecond * 60, 0);

/** Trim to the shortest form that still reads exactly. */
const fmt = (value: number, digits = 2) =>
  Number.isFinite(value) ? Number(value.toFixed(digits)).toString() : '—';

/**
 * The scenario's passive constants, in the same control row the evolution page
 * uses for `S` and `p`.
 *
 * They belong on this page because this whole chart is downstream of them:
 * passive income is the only thing filling the pool, so `a`, `n` and `b` are
 * what the settled level *is*. They write to the scenario itself, not to a
 * local copy — the same numbers the charts page and `feeding_params.lua` carry.
 */
function PassiveSliders() {
  const { scenario, setScenario } = useLab();
  const bounds = passivePowerBounds(scenario.evoPower);
  const corrected = !scenario.evoStopScalingAt100;
  const running = effectivePassivePower(scenario);
  return (
    <ParamRow>
      <ParamSlider
        label="Scaling constant"
        hint="a — the powered term's coefficient"
        value={scenario.passiveScale}
        min={0}
        max={500}
        step={5}
        format={(v) => `a = ${v.toFixed(0)}`}
        note={`${perMinute(scenario.passiveScale)} threat/min from this term at 100% evo`}
        onChange={(passiveScale) => setScenario({ passiveScale })}
      />
      <ParamSlider
        label="Scaling factor"
        hint="n — how fast income climbs with evolution"
        value={scenario.passivePower}
        min={bounds.min}
        max={bounds.max}
        step={PASSIVE_POWER_STEP}
        format={(v) => `n = ${v.toFixed(2)}`}
        note={
          corrected
            ? `runs at ${running.toFixed(3)} · 1 < n < ${(1 / scenario.evoPower).toFixed(2)}`
            : `1 < n < ${(1 / scenario.evoPower).toFixed(2)}`
        }
        onChange={(passivePower) => setScenario({ passivePower })}
      />
      <ParamSlider
        label="Linear constant"
        hint="b — the floor before E^n amounts to anything"
        value={scenario.passiveLinear}
        min={0}
        max={200}
        step={1}
        format={(v) => `b = ${v.toFixed(0)}`}
        note={`${perMinute(scenario.passiveLinear)} threat/min from this term at 100% evo`}
        onChange={(passiveLinear) => setScenario({ passiveLinear })}
      />
      <ParamActions>
        <ScenarioReset />
      </ParamActions>
    </ParamRow>
  );
}

export function WaveIllustrator() {
  const { ruleset, scenario, maxReanimThresh, maxEvo, comparing, zoomBy, resetZoom } = useLab();
  const [hover, setHover] = useState<Hover | null>(null);

  // Both panels take the wheel, the same way every chart on the charts page
  // does — the gesture cannot mean "zoom" on one plot and "scroll the page" on
  // the one directly below it.
  const thetaRef = useWheelZoom(zoomBy);
  const waveRef = useWheelZoom(zoomBy);
  // Null means "whatever this branch prices apart", so switching branches moves
  // it and an explicit click still wins.
  const [splitOverride, setSplitOverride] = useState<boolean | null>(null);

  const pricedApart = pricesKindsApart(ruleset);
  const split = splitOverride ?? pricedApart;
  const bands = useMemo(() => buildBands(split), [split]);

  /**
   * The solve is the expensive thing on this page, and it is the only thing
   * standing between a scroll notch and the next frame. Deferring its inputs
   * lets the zoom control, the axis labels and the sliders commit immediately
   * and the curve catch up a frame later — and lets React drop intermediate
   * values entirely while a wheel or a slider is still moving, so a gesture
   * costs one solve rather than one per event.
   *
   * The plot is drawn against `shownEvo`, not `maxEvo`: the window the curve
   * was solved for is the window it has to be drawn in, or the two disagree for
   * a frame and the line jumps.
   */
  const shownEvo = useDeferredValue(maxEvo);
  const shownRuleset = useDeferredValue(ruleset);

  const curve = useMemo(
    () => steadyStateByEvo(shownRuleset, gridSamples(shownEvo), maxReanimThresh),
    [shownRuleset, shownEvo, maxReanimThresh],
  );

  const maxTheta = Math.max(...curve.map((point) => point.theta), 1);
  const active = hover === null ? null : curve[hover.index];

  /** Cumulative band tops, in stacking order. */
  const tops = useMemo(() => {
    const built: Record<string, number[]> = {};
    for (const band of bands) built[band.key] = [];
    curve.forEach((point, index) => {
      let running = 0;
      for (const band of bands) {
        running += bandValue(point, band);
        built[band.key][index] = running;
      }
    });
    return built;
  }, [curve, bands]);

  const last = bands[bands.length - 1];
  const maxStack = Math.max(...tops[last.key], 1);
  const ticks = evoTicks(shownEvo);

  // The samples are a grid, not a fixed count, so the cursor lands on the
  // nearest solved evolution rather than on an index scaled from the width.
  const onMove = (event: React.MouseEvent<SVGRectElement>, panel: number) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientX - bounds.left) / Math.max(bounds.width, 1);
    const target = Math.min(Math.max(fraction, 0), 1) * shownEvo;
    let index = 0;
    let best = Infinity;
    curve.forEach((point, i) => {
      const distance = Math.abs(point.evo - target);
      if (distance < best) {
        best = distance;
        index = i;
      }
    });
    setHover({ index, panel });
  };

  return (
    <div className="wave-lab">
      {/* Same order as the evolution explorer: the constants first, then what
          they currently say and the window it is drawn in. */}
      {ruleset.id === 'scenario' ? <PassiveSliders /> : null}

      <div className="plot-head">
        <div>
          <p className="wave-assume">
            An even game — both teams on the same threat, so{' '}
            <code>⌈7 × 0.5⌉ = {EVEN_GAME_GROUPS}</code> groups per attack — with each wave killed
            off before the next arrives.
            {comparing ? ` Drawn for ${ruleset.label} only.` : ''}
          </p>
          {ruleset.id === 'scenario' ? (
            <p className="plot-formula formula">
              P = {scenario.passiveScale}·E^{fmt(effectivePassivePower(scenario), 3)} +{' '}
              {scenario.passiveLinear}·E threat per second
            </p>
          ) : null}
        </div>
        <div className="plot-zoom">
          <ZoomControls />
        </div>
      </div>

      {/* The knob that belongs to this chart rather than to the branch: whether
          the two prices in a tier are worth telling apart on screen. */}
      <label className="wave-toggle">
        <input
          type="checkbox"
          checked={split}
          onChange={(event) => setSplitOverride(event.target.checked)}
        />
        <span>
          Split biters and spitters
          <i>
            {pricedApart
              ? `${ruleset.label} prices them apart — a big biter is ${formatCompact(
                  ruleset.threatValues['big-biter'],
                  2,
                )} against ${formatCompact(ruleset.threatValues['big-spitter'], 2)}`
              : `${ruleset.label} charges the same for both, so this is the raffle's 1-in-3 and nothing more`}
          </i>
        </span>
      </label>

      <ParentSize className="wave-charts">
        {({ width }) => {
          if (width <= 0) return null;
          const innerWidth = Math.max(width - MARGIN.left - MARGIN.right, 10);
          const x = (evo: number) => (evo / (shownEvo || 1)) * innerWidth;
          const scaleY = (value: number, max: number, height: number) =>
            height - (value / max) * height;

          const thetaPath = curve
            .map(
              (p, i) => `${i === 0 ? 'M' : 'L'}${x(p.evo)} ${scaleY(p.theta, maxTheta, PLOT_HEIGHT)}`,
            )
            .join(' ');

          const bandPath = (band: Band, below: Band | null) => {
            let path = '';
            curve.forEach((point, i) => {
              path += `${i === 0 ? 'M' : 'L'}${x(point.evo)} ${scaleY(
                tops[band.key][i],
                maxStack,
                PLOT_HEIGHT,
              )} `;
            });
            for (let i = curve.length - 1; i >= 0; i -= 1) {
              path += `L${x(curve[i].evo)} ${scaleY(
                below ? tops[below.key][i] : 0,
                maxStack,
                PLOT_HEIGHT,
              )} `;
            }
            return `${path}Z`;
          };

          /**
           * The tooltip is placed from the *data*, not from the pointer: its x
           * is the cursor's evolution, so it tracks the line rather than the
           * mouse's jitter, and it needs no measuring of the container.
           */
          const tooltipFor = (panel: number) => {
            if (!active || hover?.panel !== panel) return null;
            const left = MARGIN.left + x(active.evo);
            const flip = left > width * 0.62;
            return (
              <div
                className="wave-tip"
                style={{
                  left,
                  transform: flip ? 'translateX(calc(-100% - 14px))' : 'translateX(14px)',
                }}
              >
                <b className="wave-tip-head">{formatEvo(active.evo)} evolution</b>
                <span>
                  <b>{formatCompact(active.theta, 2)}</b> threat held
                </span>
                <span>
                  <b>{formatCompact(active.income, 2)}</b> earned per attack
                </span>
                <span>
                  <b>{active.wave.unitCount.toFixed(0)}</b> units
                  {active.wave.bossCount >= 0.05 ? (
                    <>
                      {' '}
                      · <b>{active.wave.bossCount.toFixed(1)}</b> bosses
                    </>
                  ) : null}
                </span>
                <span className="wave-tip-mix">{mixLabel(active, split)}</span>
                {active.wave.bossCount >= 0.05 ? (
                  <span className="wave-tip-mix">bosses: {bossLabel(active, split)}</span>
                ) : null}
              </div>
            );
          };

          const cursorLine = (colour: string) =>
            active ? (
              <line
                x1={x(active.evo)}
                x2={x(active.evo)}
                y1={0}
                y2={PLOT_HEIGHT}
                stroke={colour}
              />
            ) : null;

          const hitArea = (panel: number) => (
            <rect
              x={0}
              y={0}
              width={innerWidth}
              height={PLOT_HEIGHT}
              fill="transparent"
              onMouseMove={(event) => onMove(event, panel)}
              onMouseLeave={() => setHover(null)}
            />
          );

          return (
            <>
              <div className="wave-panel">
                <h5 className="wave-chart-title">
                  Threat held <span>the level passive income settles at</span>
                </h5>
                <svg
                  ref={thetaRef}
                  width={width}
                  height={PLOT_HEIGHT + MARGIN.top + 4}
                  role="img"
                  aria-label="Settled threat by evolution"
                  onDoubleClick={resetZoom}
                >
                  <PlotFrame
                    innerWidth={innerWidth}
                    height={PLOT_HEIGHT}
                    ticks={yTicks(maxTheta, PLOT_HEIGHT, 3)}
                    format={(value) => formatCompact(value, 0)}
                  >
                    <path
                      d={`${thetaPath} L${x(shownEvo)} ${PLOT_HEIGHT} L0 ${PLOT_HEIGHT} Z`}
                      fill={THREAT_COLOUR}
                      opacity={0.13}
                    />
                    <path d={thetaPath} fill="none" stroke={THREAT_COLOUR} strokeWidth={2} />
                    {cursorLine('#8b97ad')}
                    {active ? (
                      <circle
                        cx={x(active.evo)}
                        cy={scaleY(active.theta, maxTheta, PLOT_HEIGHT)}
                        r={4}
                        fill={THREAT_COLOUR}
                        stroke={SURFACE}
                        strokeWidth={2}
                      />
                    ) : null}
                    {hitArea(0)}
                  </PlotFrame>
                </svg>
                {tooltipFor(0)}
              </div>

              <div className="wave-panel">
                <h5 className="wave-chart-title">
                  What one attack draws <span>units per attack, a boss counting as 20</span>
                </h5>
                <svg
                  ref={waveRef}
                  width={width}
                  height={PLOT_HEIGHT + MARGIN.top + AXIS_HEIGHT}
                  role="img"
                  aria-label="Wave composition by evolution"
                  onDoubleClick={resetZoom}
                >
                  {/* A boss is the same hue as its tier, cut with the surface
                      colour — so "which tier" and "boss or not" are two
                      channels rather than one palette of eight. */}
                  <defs>
                    {bands
                      .filter((band) => band.boss)
                      .map((band) => (
                        <pattern
                          key={band.key}
                          id={band.key}
                          width={6}
                          height={6}
                          patternUnits="userSpaceOnUse"
                          patternTransform="rotate(45)"
                        >
                          <rect width={6} height={6} fill={band.colour} />
                          <line
                            x1={0}
                            y1={0}
                            x2={0}
                            y2={6}
                            stroke={SURFACE}
                            strokeWidth={2.6}
                          />
                        </pattern>
                      ))}
                  </defs>
                  <PlotFrame
                    innerWidth={innerWidth}
                    height={PLOT_HEIGHT}
                    ticks={yTicks(maxStack, PLOT_HEIGHT, 3)}
                    format={(value) => value.toFixed(0)}
                  >
                    {bands.map((band, index) => (
                      <path
                        key={band.key}
                        d={bandPath(band, index === 0 ? null : bands[index - 1])}
                        fill={band.fill}
                        opacity={0.9}
                        // A hairline of the surface colour between bands, so
                        // two adjacent ones never bleed into a single shape.
                        stroke={SURFACE}
                        strokeWidth={1}
                      >
                        <title>{band.label}</title>
                      </path>
                    ))}
                    {cursorLine('#e8eefc')}
                    {ticks.map((evo) => (
                      <text
                        key={evo}
                        x={x(evo)}
                        y={PLOT_HEIGHT + 14}
                        textAnchor={evo === 0 ? 'start' : 'middle'}
                        className="wave-axis-text"
                      >
                        {formatEvo(evo, 0)}
                      </text>
                    ))}
                    {hitArea(1)}
                  </PlotFrame>
                </svg>
                {tooltipFor(1)}
              </div>
            </>
          );
        }}
      </ParentSize>

      <div className="wave-legend">
        {STRONGEST_FIRST.map((tier) => (
          <span key={tier}>
            <i style={{ background: TIER_COLOURS[tier] }} />
            {TIER_LABEL[tier]}
          </span>
        ))}
        {split ? (
          <span>
            <i style={{ background: lighten('#8b97ad', SPITTER_LIGHTEN) }} />
            Lighter — spitter
          </span>
        ) : null}
        <span>
          {/* backgroundColor, not background: the shorthand would wipe the
              hatch the stylesheet paints over it. */}
          <i className="wave-key-hatch" style={{ backgroundColor: '#8b97ad' }} />
          Hatched — boss, counts as 20
        </span>
        <span className="wave-legend-note">
          strongest at the bottom · hover for the numbers · scroll to zoom
        </span>
      </div>
    </div>
  );
}

/** "51 behemoth · 39 big", strongest first, skipping tiers that round to none. */
function mixLabel(point: SteadyState, split: boolean): string {
  const parts: string[] = [];
  for (const tier of STRONGEST_FIRST) {
    if (point.wave.units[tier] < 0.5) continue;
    if (split) {
      for (const kind of UNIT_KINDS) {
        const count = point.wave.unitsByKind[kind][tier];
        if (count >= 0.5) parts.push(`${count.toFixed(0)} ${TIER_LABEL[tier].toLowerCase()} ${kind}`);
      }
    } else {
      parts.push(`${point.wave.units[tier].toFixed(0)} ${TIER_LABEL[tier].toLowerCase()}`);
    }
  }
  return parts.length ? parts.join(' · ') : 'no wave';
}

/** The same, for the boss half of the wave — which tiers the budget reached. */
function bossLabel(point: SteadyState, split: boolean): string {
  const parts: string[] = [];
  for (const tier of STRONGEST_FIRST) {
    if (point.wave.bosses[tier] < 0.05) continue;
    if (split) {
      for (const kind of UNIT_KINDS) {
        const count = point.wave.bossesByKind[kind][tier];
        if (count >= 0.05) parts.push(`${count.toFixed(1)} ${TIER_LABEL[tier].toLowerCase()} ${kind}`);
      }
    } else {
      parts.push(`${point.wave.bosses[tier].toFixed(1)} ${TIER_LABEL[tier].toLowerCase()}`);
    }
  }
  return parts.length ? parts.join(' · ') : 'none';
}
